<template>
  <div v-if="dialogVisible" class="changelog-overlay">
    <div class="changelog-dialog">
      <header class="changelog-header">
        <div class="changelog-header__title-wrap">
          <i class="el-icon-time changelog-header__icon"></i>
          <h1 class="changelog-header__title">
            {{ $t("header.changelog.title") }}
          </h1>
        </div>
        <button
          class="changelog-close"
          type="button"
          :aria-label="$t('header.changelog.close')"
          @click="closeDialog"
        >
          ✕
        </button>
      </header>

      <div class="changelog-body">
        <!-- 列表为空：整区缺省占位 -->
        <div
          v-if="showEmptyPlaceholder"
          class="changelog-empty"
        >
          <i class="el-icon-document changelog-empty__icon"></i>
          <div class="changelog-empty__text">
            {{ $t("header.changelog.empty") }}
          </div>
          <div class="changelog-empty__desc">
            {{ $t("header.changelog.emptyTip") }}
          </div>
        </div>

        <template v-else>
          <!-- 左侧时间轴：按发布顺序切换公告 -->
          <aside
            class="changelog-timeline"
            ref="timelinePanel"
            @scroll="handleTimelineScroll"
          >
            <button
              v-for="(item, index) in changelogItems"
              :key="item.id != null ? item.id : index"
              type="button"
              class="changelog-timeline__item"
              :class="{ active: index === activeIndex }"
              @click="selectEntry(index)"
            >
              <span class="changelog-timeline__dot"></span>
              <span class="changelog-timeline__meta">
                <span class="changelog-timeline__date">
                  {{ formatPublishDate(item.publishTime) }}
                </span>
                <span class="changelog-timeline__entry-title">
                  {{ item.title || $t("header.changelog.fallbackTitle") }}
                </span>
              </span>
            </button>
            <div v-if="loading" class="changelog-timeline__status">
              {{ $t("header.changelog.loading") }}
            </div>
            <div
              v-else-if="loadError && !changelogItems.length"
              class="changelog-timeline__status changelog-timeline__status--error"
            >
              <span>{{ $t("header.changelog.loadFailed") }}</span>
              <button
                type="button"
                class="changelog-timeline__retry"
                @click="fetchAnnouncementPage(true)"
              >
                {{ $t("header.changelog.retry") }}
              </button>
            </div>
          </aside>

          <!-- 右侧：当前选中公告的标题与内容 -->
          <section class="changelog-content" ref="contentPanel">
            <div v-if="loading && !activeEntry" class="changelog-content__empty">
              {{ $t("header.changelog.loading") }}
            </div>
            <div
              v-else-if="loadError && !activeEntry"
              class="changelog-content__empty"
            >
              {{ $t("header.changelog.loadFailed") }}
            </div>
            <div v-else-if="activeEntry" class="changelog-content__inner">
              <div class="changelog-content__badge-row">
                <span
                  v-if="activeEntry.publishTime"
                  class="changelog-content__date"
                >
                  {{ formatPublishTime(activeEntry.publishTime) }}
                </span>
                <span
                  v-if="activeEntry.projectVersion"
                  class="changelog-content__version"
                >
                  {{ activeEntry.projectVersion }}
                </span>
              </div>
              <h2 class="changelog-content__title">
                {{ activeEntry.title || $t("header.changelog.fallbackTitle") }}
              </h2>
              <!-- 公告正文由后端下发，与登录公告一致按 HTML 渲染 -->
              <div
                class="changelog-content__body"
                v-html="activeEntry.content || ''"
              ></div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/** 更新日志单页条数 */
const PAGE_SIZE = 20;

export default {
  name: "ChangelogDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 当前选中的时间轴索引
      activeIndex: 0,
      // 分页公告列表（接口字段：id / projectVersion / title / content / publishTime）
      changelogItems: [],
      pageNum: 1,
      pageSize: PAGE_SIZE,
      total: 0,
      pages: 0,
      loading: false,
      loadError: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    activeEntry() {
      return this.changelogItems[this.activeIndex] || null;
    },
    /** 是否还有下一页可加载 */
    hasMore() {
      if (!this.pages) {
        return this.changelogItems.length < this.total;
      }
      return this.pageNum < this.pages;
    },
    /** 请求结束且 list 为空时展示缺省占位 */
    showEmptyPlaceholder() {
      return (
        !this.loading &&
        !this.loadError &&
        (!this.changelogItems || this.changelogItems.length === 0)
      );
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.activeIndex = 0;
        // 打开时拉取第一页，并监听 Esc
        this.resetAndFetch();
        window.addEventListener("keydown", this.handleGlobalKeydown);
      } else {
        window.removeEventListener("keydown", this.handleGlobalKeydown);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalKeydown);
  },
  methods: {
    /** 关闭全屏更新日志 */
    closeDialog() {
      this.dialogVisible = false;
    },
    /** 全局快捷键：Esc 关闭全屏弹窗 */
    handleGlobalKeydown(event) {
      if (event.key === "Escape" && this.dialogVisible) {
        event.preventDefault();
        this.closeDialog();
      }
    },
    /** 切换左侧时间轴选中项，并重置右侧滚动位置 */
    selectEntry(index) {
      this.activeIndex = index;
      this.$nextTick(() => {
        const panel = this.$refs.contentPanel;
        if (panel) {
          panel.scrollTop = 0;
        }
      });
    },
    /** 打开弹窗时重置分页状态并请求第一页 */
    resetAndFetch() {
      this.changelogItems = [];
      this.pageNum = 1;
      this.total = 0;
      this.pages = 0;
      this.activeIndex = 0;
      this.loadError = false;
      this.fetchAnnouncementPage(true);
    },
    /**
     * 分页查询全部公告（只读，不推进登录公告进度）
     * GET /ytb/announcement/page
     * @param {boolean} reset 是否替换列表（首屏/重试）
     */
    async fetchAnnouncementPage(reset = false) {
      if (this.loading) {
        return;
      }
      if (!reset && !this.hasMore) {
        return;
      }
      const requestPage = reset ? 1 : this.pageNum + 1;
      this.loading = true;
      this.loadError = false;
      try {
        const res = await this.$axios({
          method: "get",
          url: this.$store.state.serverUrl + "ytb/announcement/page",
          params: {
            pageNum: requestPage,
            pageSize: this.pageSize,
          },
        });
        if (!res || !res.data || res.data.code != 200) {
          this.loadError = true;
          if (reset) {
            this.changelogItems = [];
          }
          return;
        }
        const data = res.data.data || {};
        const list = Array.isArray(data.list) ? data.list : [];
        // 仅保留接口约定字段，避免混入无关属性
        const mapped = list.map((item) => ({
          id: item.id,
          projectVersion: item.projectVersion || "",
          title: item.title || "",
          content: item.content || "",
          publishTime: item.publishTime || "",
        }));
        this.changelogItems = reset
          ? mapped
          : this.changelogItems.concat(mapped);
        this.pageNum = Number(data.pageNum) || requestPage;
        this.pageSize = Number(data.pageSize) || this.pageSize;
        this.total = Number(data.total) || 0;
        this.pages = Number(data.pages) || 0;
        if (reset) {
          this.activeIndex = 0;
        }
        // 内容未撑满滚动区时继续拉取，避免无法触底加载
        this.$nextTick(() => {
          this.loadMoreIfTimelineNotScrollable();
        });
      } catch (error) {
        this.loadError = true;
        if (reset) {
          this.changelogItems = [];
        }
      } finally {
        this.loading = false;
      }
    },
    /** 时间轴接近底部（桌面）或右端（移动端横滑）时自动加载下一页 */
    handleTimelineScroll() {
      const panel = this.$refs.timelinePanel;
      if (!panel || this.loading || !this.hasMore) {
        return;
      }
      const isHorizontal = panel.scrollWidth > panel.clientWidth + 1;
      if (isHorizontal) {
        const distanceToEnd =
          panel.scrollWidth - panel.scrollLeft - panel.clientWidth;
        if (distanceToEnd <= 48) {
          this.fetchAnnouncementPage(false);
        }
        return;
      }
      const distanceToBottom =
        panel.scrollHeight - panel.scrollTop - panel.clientHeight;
      if (distanceToBottom <= 48) {
        this.fetchAnnouncementPage(false);
      }
    },
    /** 时间轴尚无溢出滚动时主动续页，保证后续数据能被拉到 */
    loadMoreIfTimelineNotScrollable() {
      const panel = this.$refs.timelinePanel;
      if (!panel || this.loading || !this.hasMore) {
        return;
      }
      const canScrollY = panel.scrollHeight > panel.clientHeight + 1;
      const canScrollX = panel.scrollWidth > panel.clientWidth + 1;
      if (!canScrollY && !canScrollX) {
        this.fetchAnnouncementPage(false);
      }
    },
    /** 时间轴展示用短日期 */
    formatPublishDate(publishTime) {
      const formatted = this.formatPublishTime(publishTime);
      if (!formatted) {
        return "";
      }
      return formatted.slice(0, 10);
    },
    /** 将 publishTime 格式化为可读时间；解析失败则原样返回 */
    formatPublishTime(publishTime) {
      const raw = publishTime ? String(publishTime).trim() : "";
      if (!raw) {
        return "";
      }
      const date = new Date(raw.replace(/-/g, "/"));
      if (Number.isNaN(date.getTime())) {
        return raw;
      }
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate(),
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style lang="less" scoped>
.changelog-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.96);
  color: #f8fafc;
}

.changelog-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  outline: none;
  background:
    radial-gradient(
      circle at top center,
      rgba(109, 56, 255, 0.16),
      transparent 30%
    ),
    #06070b;
}

.changelog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 28px 40px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.changelog-header__title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.changelog-header__icon {
  color: #a78bfa;
  font-size: 22px;
}

.changelog-header__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #f8fafc;
  letter-spacing: 0.02em;
}

.changelog-close {
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }
}

.changelog-body {
  display: flex;
  flex: 1;
  min-height: 0;
  padding: 0 40px 40px;
}

.changelog-timeline {
  flex-shrink: 0;
  width: 240px;
  padding: 28px 24px 28px 8px;
  overflow-y: auto;
  border-right: 1px solid rgba(255, 255, 255, 0.06);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 4px;
  }
}

.changelog-timeline__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 14px 12px 14px 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #8b93a7;
  transition: color 0.18s ease;

  &::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 28px;
    bottom: -14px;
    width: 1px;
    background: rgba(255, 255, 255, 0.08);
  }

  &:last-of-type::before {
    display: none;
  }

  &:hover {
    color: #e2e8f0;
  }

  &.active {
    color: #f8fafc;

    .changelog-timeline__dot {
      background: #8b5cf6;
      box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.22);
    }

    .changelog-timeline__entry-title {
      color: #c4b5fd;
    }
  }
}

.changelog-timeline__dot {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 11px;
  height: 11px;
  margin-top: 4px;
  border-radius: 50%;
  background: #3f4558;
  transition: all 0.18s ease;
}

.changelog-timeline__meta {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  min-width: 0;
}

.changelog-timeline__date {
  font-size: 14px;
  font-weight: 650;
  line-height: 1.3;
}

.changelog-timeline__entry-title {
  display: block;
  overflow: hidden;
  font-size: 12px;
  color: #6f778b;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.18s ease;
}

.changelog-timeline__status {
  padding: 12px 0 4px 25px;
  font-size: 12px;
  color: #6f778b;
  line-height: 1.5;
}

.changelog-timeline__status--error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  color: #f87171;
}

.changelog-timeline__retry {
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.changelog-content {
  flex: 1;
  min-width: 0;
  padding: 32px 8px 8px 40px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 6px;
  }
}

.changelog-content__badge-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.changelog-content__date {
  font-size: 13px;
  color: #8b93a7;
}

.changelog-content__version {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.18);
  color: #e9d5ff;
  font-size: 12px;
  font-weight: 650;
}

.changelog-content__title {
  margin: 0 0 20px;
  font-size: 28px;
  font-weight: 750;
  line-height: 1.3;
  color: #ffffff;
}

.changelog-content__body {
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;

  /deep/ * {
    background-color: transparent !important;
    color: #ffffff !important;
  }

  /deep/ p {
    margin: 0 0 12px;
  }

  /deep/ ul,
  /deep/ ol {
    margin: 0 0 12px;
    padding-left: 20px;
  }

  /deep/ li {
    margin-bottom: 6px;
  }

  /deep/ a {
    color: #c4b5fd;
  }

  /deep/ img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.changelog-content__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #6f778b;
  font-size: 14px;
}

.changelog-empty {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 40px 20px;
}

.changelog-empty__icon {
  margin-bottom: 16px;
  color: #4b5563;
  font-size: 48px;
}

.changelog-empty__text {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #e2e8f0;
}

.changelog-empty__desc {
  font-size: 14px;
  color: #6b7280;
}

@media (max-width: 768px) {
  .changelog-header {
    padding: 20px 20px 16px;
  }

  .changelog-body {
    flex-direction: column;
    padding: 0 16px 24px;
  }

  .changelog-timeline {
    width: 100%;
    display: flex;
    gap: 8px;
    padding: 16px 0;
    overflow-x: auto;
    overflow-y: hidden;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  .changelog-timeline__item {
    flex-shrink: 0;
    width: 180px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.03);

    &::before {
      display: none;
    }

    &.active {
      background: rgba(124, 58, 237, 0.18);
    }
  }

  .changelog-timeline__dot {
    margin-top: 3px;
  }

  .changelog-timeline__status {
    flex-shrink: 0;
    padding: 10px 8px;
    align-self: center;
  }

  .changelog-content {
    padding: 20px 0 0;
  }

  .changelog-content__title {
    font-size: 22px;
  }
}
</style>
